import { isInternalHost, pages } from './links'

// Matches absolute http(s) URLs and relative event paths like `/agenda/{slug}`.
// Captures the full matched URL/path as group 1.
const URL_RE =
  /((?:https?:\/\/[^\s<>"']+|\/agenda\/[^\s<>"']+))/gi

export type ContentToken = {
  type: 'text' | 'url'
  value: string
  href?: string
}

// Splits a content string into ordered text and URL tokens.
export function tokenizeContent(content: string): ContentToken[] {
  const tokens: ContentToken[] = []
  let lastIndex = 0

  for (const m of content.matchAll(URL_RE)) {
    const before = content.slice(lastIndex, m.index)
    if (before) {
      tokens.push({ type: 'text', value: before })
    }
    const match = m[1]
    const rawUrl = match.replace(/[.,;!?()[\]{}]+$/g, '')
    const trailing = match.slice(rawUrl.length)
    const href = normalizeForLink(rawUrl)
    tokens.push({
      type: 'url',
      value: rawUrl,
      ...(href !== rawUrl ? { href } : {}),
    })
    if (trailing) {
      tokens.push({ type: 'text', value: trailing })
    }
    lastIndex = (m.index ?? 0) + match.length
  }

  const remainder = content.slice(lastIndex)
  if (remainder) {
    tokens.push({ type: 'text', value: remainder })
  }

  return tokens
}

// Converts an internal absolute URL to its relative path so LinkAnchor uses SPA
// navigation. External URLs are left as-is.
function normalizeForLink(rawUrl: string): string {
  // Already relative — use as-is
  if (rawUrl.startsWith('/')) {
    return rawUrl
  }

  try {
    const u = new URL(rawUrl)
    if (isInternalHost(u.host)) {
      return u.pathname + u.search + u.hash
    }
  } catch {
    // Malformed URL — return as-is
  }

  return rawUrl
}

// Extracts ordered, de-duplicated event slugs from a content string.
// Only returns slugs from URLs whose host is internal (or relative paths).
export function extractEventSlugs(content: string): string[] {
  const slugs: string[] = []
  const seen = new Set<string>()

  for (const m of content.matchAll(URL_RE)) {
    const match = m[1]
    const raw = match.replace(/[.,;!?()[\]{}]+$/g, '')
    const slug = getEventSlug(raw)
    if (slug && !seen.has(slug)) {
      seen.add(slug)
      slugs.push(slug)
    }
  }

  return slugs
}

// Returns the event slug if rawUrl points to an internal event detail page;
// otherwise null. Handles absolute (http(s)://host/agenda/{slug}) and relative
// (/agenda/{slug}) forms. Strips query, hash, and trailing slashes.
// Excludes the reserved "create" sub-segment.
export function getEventSlug(rawUrl: string): string | null {
  let pathname: string

  if (rawUrl.startsWith('/')) {
    pathname = rawUrl
  } else {
    try {
      const u = new URL(rawUrl)
      if (!isInternalHost(u.host)) {
        return null
      }
      pathname = u.pathname
    } catch {
      return null
    }
  }

  // Strip query and hash
  const qIdx = pathname.indexOf('?')
  const hIdx = pathname.indexOf('#')
  let clean = pathname
  if (qIdx !== -1) clean = clean.slice(0, qIdx)
  if (hIdx !== -1) clean = clean.slice(0, hIdx)

  // Strip trailing slash
  clean = clean.replace(/\/$/, '')

  // Must be exactly /agenda/{slug}
  const prefix = pages.events + '/'
  if (!clean.startsWith(prefix)) {
    return null
  }

  const slug = clean.slice(prefix.length)

  // Must have exactly one path segment and can't be empty or reserved
  if (!slug || slug.includes('/')) {
    return null
  }

  if (slug === 'create') {
    return null
  }

  return slug
}
